import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Badge, Modal } from '../../components/ui';
import { 
  MapPin, Globe, Facebook, Instagram, Twitter, Calendar, 
  Users, ArrowLeft, Send, Loader2, CheckCircle, AlertTriangle,
  Home, Edit
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Footer from '../../components/layout/Footer';
import ProfileCompletionModal from '../../components/user/ProfileCompletionModal';
import CampNotFound from '../CampNotFound';

interface CampCategory {
  _id: string;
  name: string;
}

interface GlobalPerk {
  _id: string;
  name: string;
  icon: string;
  color: string;
}

interface SelectedPerk {
  perkId: string;
  isOn: boolean;
  offering?: GlobalPerk; // Populated perk data
}

interface CampPhoto {
  url: string;
  caption: string;
  isPrimary: boolean;
}

interface Camp {
  _id: string;
  campName: string;
  slug: string;
  description?: string;
  bio?: string;
  theme?: string;
  hometown?: string;
  burningSince?: number;
  contactEmail?: string;
  website?: string;
  isPubliclyVisible?: boolean;
  acceptingApplications?: boolean;
  applicationInstructions?: string;
  isCampAdmin?: boolean; // Flag from backend indicating user is camp admin
  isSystemAdmin?: boolean; // Flag from backend indicating user is system admin
  categories?: CampCategory[]; // Updated to include full category objects
  selectedPerks?: SelectedPerk[]; // Array of selected perks with populated data
  // Legacy offerings object for backward compatibility
  offerings?: {
    water?: boolean;
    fullPower?: boolean;
    partialPower?: boolean;
    rvPower?: boolean;
    acceptsRVs?: boolean;
    shadeForTents?: boolean;
    showers?: boolean;
    communalKitchen?: boolean;
    storage?: boolean;
    wifi?: boolean;
    ice?: boolean;
    food?: boolean;
    coffee?: boolean;
    bar?: boolean;
    snacks?: boolean;
    music?: boolean;
    art?: boolean;
    workshops?: boolean;
    performances?: boolean;
    games?: boolean;
    yoga?: boolean;
    meditation?: boolean;
    bikeRepair?: boolean;
    massage?: boolean;
    hairStyling?: boolean;
    facePainting?: boolean;
    costumeRental?: boolean;
    sharedSpace?: boolean;
    campfire?: boolean;
    socialEvents?: boolean;
    welcomeNewbies?: boolean;
  };
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
  };
  location?: {
    street?: string;
    crossStreet?: string;
    time?: string;
    description?: string;
  };
  photos?: (string | CampPhoto)[]; // Support both legacy string format and new object format
  primaryPhotoIndex?: number;
  stats?: {
    totalMembers?: number;
  };
}

const hasApplicationProfileCity = (user: any): boolean => (
  Boolean(user?.city?.trim()) || Boolean(user?.location?.city?.trim())
);

const isApplicationProfileIncomplete = (user: any): boolean => (
  !user.firstName ||
  !user.lastName ||
  !user.playaName ||
  !hasApplicationProfileCity(user) ||
  !user.phoneCountryCode ||
  !user.phoneNumber ||
  !user.skills ||
  user.skills.length === 0 ||
  !user.foodPreferences ||
  user.foodPreferences.length === 0 ||
  user.yearsBurned === undefined ||
  !user.burningPlans
);

// Helper function to dynamically render Lucide icons
const renderIcon = (iconName: string) => {
  const IconComponent = (LucideIcons as any)[iconName];
  if (IconComponent) {
    return <IconComponent className="w-4 h-4" />;
  }
  return null;
};

const PublicCampProfile: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [camp, setCamp] = useState<Camp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isNotFound, setIsNotFound] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [availableCallSlots, setAvailableCallSlots] = useState<any[]>([]);
  const [showProfileCompletionModal, setShowProfileCompletionModal] = useState(false);
  const [hasAutoOpenedApply, setHasAutoOpenedApply] = useState(false);
  
  // Capture invite token from URL if present
  const inviteToken = searchParams.get('invite_token') || searchParams.get('invite');
  const shouldAutoApply = searchParams.get('apply') === '1';
  
  // Application form state
  const [applicationData, setApplicationData] = useState({
    motivation: '',
    experience: '',
    skills: [] as string[],
    selectedCallSlotId: ''
  });

  useEffect(() => {
    if (slug) {
      fetchCamp();
    }
  }, [slug]);

  // Log invite token detection for debugging
  useEffect(() => {
    if (inviteToken) {
      console.log('🎟️ [PublicCampProfile] Invite token detected:', inviteToken);
    }
  }, [inviteToken]);
  
  // Check if profile completion modal should be shown
  useEffect(() => {
    if (!user || !inviteToken) return;
    
    // Check if user has a pending invite in localStorage
    const pendingInvite = localStorage.getItem('pendingInvite');
    if (!pendingInvite) return;
    
    const isProfileIncomplete = isApplicationProfileIncomplete(user);
    
    if (isProfileIncomplete) {
      console.log('🎟️ [PublicCampProfile] Showing profile completion modal');
      setShowProfileCompletionModal(true);
    }
  }, [user, inviteToken]);

  const fetchCamp = async () => {
    try {
      setLoading(true);
      setError('');
      setIsNotFound(false);
      
      console.log('🔍 [PublicCampProfile] Fetching camp with slug:', slug);
      const response = await api.get(`/camps/public/${slug}`);
      console.log('✅ [PublicCampProfile] Camp API response:', JSON.stringify(response, null, 2));
      console.log('🔍 [PublicCampProfile] Camp name:', response.campName);
      console.log('🔍 [PublicCampProfile] Camp photos:', response.photos);
      console.log('🔍 [PublicCampProfile] Primary photo index:', response.primaryPhotoIndex);
      console.log('🔍 [PublicCampProfile] Photo at index:', response.photos?.[response.primaryPhotoIndex || 0]);
      console.log('🔍 [PublicCampProfile] Accepting applications:', response.acceptingApplications);
      setCamp(response);
    } catch (err: any) {
      console.error('❌ [PublicCampProfile] Error fetching camp:', err);
      if (err.response?.status === 404) {
        setIsNotFound(true);
      }
      setError(err.response?.data?.message || 'Failed to load camp profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileComplete = () => {
    console.log('✅ [PublicCampProfile] Profile completed');
    setShowProfileCompletionModal(false);
    // Clear the pending invite from localStorage
    localStorage.removeItem('pendingInvite');
    // Optionally show a success message
    alert('Profile completed! You can now apply to this camp.');
  };
  
  const handleApplyNow = async () => {
    if (!user) {
      const baseCampPath = `/camps/${slug || camp?.slug || camp?._id}`;
      const params = new URLSearchParams();
      params.set('apply', '1');
      if (inviteToken) {
        params.set('invite_token', inviteToken);
      }
      const redirectTarget = `${baseCampPath}?${params.toString()}`;
      const wantsToCreateAccount = window.confirm(
        'To apply to this camp, you need an account.\n\nPress OK to create an account, or Cancel to log in.'
      );
      window.location.href = wantsToCreateAccount
        ? `/register?redirect=${encodeURIComponent(redirectTarget)}`
        : `/login?redirect=${encodeURIComponent(redirectTarget)}`;
      return;
    }
    
    if (user.accountType !== 'personal') {
      alert('Only personal accounts can apply to camps');
      return;
    }
    
    const isProfileIncomplete = isApplicationProfileIncomplete(user);
      
    if (isProfileIncomplete) {
      setShowProfileCompletionModal(true);
      return;
    }
    
    // Fetch available call slots
    try {
      const slots = await api.get(`/call-slots/available/${camp?._id}`);
      setAvailableCallSlots(slots || []);
    } catch (error) {
      console.error('Error fetching call slots:', error);
      setAvailableCallSlots([]);
    }
    
    setShowApplicationModal(true);
  };

  useEffect(() => {
    if (hasAutoOpenedApply || !camp || !shouldAutoApply || !user || user.accountType !== 'personal') {
      return;
    }
    setHasAutoOpenedApply(true);
    void handleApplyNow();
  }, [camp, hasAutoOpenedApply, shouldAutoApply, user]);

  const handleSubmitApplication = async () => {
    try {
      setApplicationLoading(true);
      console.log('🔄 [PublicCampProfile] Submitting application to camp:', camp?._id);
      if (inviteToken) {
        console.log('🎟️ [PublicCampProfile] Application includes invite token:', inviteToken);
      }
      
      const response = await api.post('/applications/apply', {
        campId: camp?._id,
        applicationData: {
          motivation: applicationData.motivation,
          experience: applicationData.experience,
          skills: applicationData.skills,
          selectedCallSlotId: applicationData.selectedCallSlotId || undefined
        },
        inviteToken: inviteToken || undefined // Pass invite token if present
      });
      
      console.log('✅ [PublicCampProfile] Application submitted successfully:', response);
      
      setApplicationSuccess(true);
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setShowApplicationModal(false);
        setApplicationSuccess(false);
        setApplicationData({ motivation: '', experience: '', skills: [], selectedCallSlotId: '' });
      }, 3500);
      
    } catch (err: any) {
      console.error('❌ [PublicCampProfile] Application error:', err);
      console.error('❌ [PublicCampProfile] Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || 'Failed to submit application';
      alert(errorMessage);
    } finally {
      setApplicationLoading(false);
    }
  };

  const getOfferingIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      CheckCircle: <CheckCircle className="w-4 h-4" />,
      Home: <Home className="w-4 h-4" />,
      Coffee: <CheckCircle className="w-4 h-4" />,
      Wifi: <CheckCircle className="w-4 h-4" />,
      Music: <CheckCircle className="w-4 h-4" />,
      Dumbbell: <CheckCircle className="w-4 h-4" />,
      Waves: <CheckCircle className="w-4 h-4" />,
      Zap: <CheckCircle className="w-4 h-4" />
    };
    return iconMap[iconName] || <CheckCircle className="w-4 h-4" />;
  };

  const formatOfferingName = (key: string) => {
    return key.replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .trim();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-custom-primary" />
        </div>
      </div>
    );
  }

  // Show custom 404 page for not found or private camps
  // BUT allow camp admins and system admins to view private camps
  if (isNotFound && !camp?.isCampAdmin && !camp?.isSystemAdmin) {
    return <CampNotFound />;
  }
  
  // If camp is not found but user is system admin or camp admin, try to fetch anyway
  // This handles the case where the API returns 404 but user should have access
  if (isNotFound && (camp?.isCampAdmin || camp?.isSystemAdmin)) {
    // Clear the not found flag to allow rendering
    setIsNotFound(false);
  }

  // Show generic error for other errors
  if (error || !camp) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-16">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-h3 font-lato-bold text-custom-text-secondary mb-2">
            {error || 'Camp not found'}
          </h2>
          <Button onClick={() => navigate('/camps')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Camps
          </Button>
        </div>
      </div>
    );
  }


  // ============================================================================
  // CRITICAL: Camp Ownership Check Must Use IDs, Never Slugs or URL Matching
  // ============================================================================
  // Camp ownership (user.campId === camp._id) is the ONLY source of truth.
  // 
  // WHY URL alone must never determine edit permissions:
  // 1. Security: URLs can be guessed, manipulated, or spoofed
  // 2. Slugs can change: Camp renames change slug, breaking slug-based checks
  // 3. User data unreliable: user.urlSlug doesn't exist, user.campName is inconsistent
  // 4. Backend has authority: Database relationship is definitive
  // 
  // INCORRECT (old logic):
  // - Comparing user.urlSlug with camp slug (user.urlSlug doesn't exist)
  // - Generating slug from user.campName and comparing (inconsistent)
  // - URL-based authentication (insecure)
  // 
  // CORRECT (new logic):
  // - Compare camp._id with user.campId (admin users)
  // - Compare camp._id with user._id (camp user is the camp entity)
  // - Trust backend flags: camp.isCampAdmin (set by backend based on DB)
  // 
  // This ensures:
  // - Secure: Only actual owners can edit
  // - Consistent: Works for OAuth, password, impersonation
  // - Reliable: Based on database relationships, not URLs
  // ============================================================================
  const isCampOwner = (() => {
    if (!user || !camp) return false;
    
    // Method 1: Trust backend flag (most reliable)
    // Backend sets camp.isCampAdmin based on database relationships
    if (camp.isCampAdmin === true) {
      console.log('✅ [PublicCampProfile] User is camp admin (backend flag)');
      return true;
    }
    
    // Method 2: Fallback to manual ID comparison
    // Admin users: compare campId with camp's _id
    if (user.accountType === 'admin' && user.campId) {
      const isOwner = camp._id === user.campId;
      console.log('🔍 [PublicCampProfile] Admin ownership check:', { campId: camp._id, userCampId: user.campId, isOwner });
      return isOwner;
    }
    
    // Camp users: compare canonical user.campId with camp _id.
    // user._id is the user identity, not camp ownership identity.
    if (user.accountType === 'camp') {
      const isOwner = camp._id === user.campId;
      console.log('🔍 [PublicCampProfile] Camp ownership check:', { campId: camp._id, userCampId: user.campId, isOwner });
      return isOwner;
    }
    
    console.log('⚠️ [PublicCampProfile] Not a camp owner');
    return false;
  })();
  const isEventsLeadViewingAssignedCamp = Boolean(
    user?.isEventsLead &&
      camp &&
      (
        user.eventsLeadCampId === camp._id ||
        user.eventsLeadCampSlug === camp.slug ||
        user.eventsLeadCampSlug === slug
      )
  );
  const isShiftsOnlyApplicationLocked = Boolean(
    (user as any)?.isShiftsOnlyMember && !(user as any)?.canApplyToCampsNow
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Private Profile Banner - Only shown to camp admin when profile is not public */}
      {/* System admins (including impersonated) can view but don't need the banner */}
      {camp.isCampAdmin && !camp.isSystemAdmin && camp.isPubliclyVisible === false && (
        <div className="mb-6 bg-orange-50 border-2 border-orange-300 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-orange-900 mb-2">
                Your Camp Profile is Currently Private
              </h3>
              <p className="text-orange-800 mb-4">
                Your camp profile is not visible to the public yet. To make it discoverable on the camps page and start accepting applications, you need to set it to public.
              </p>
              <Button 
                onClick={() => {
                  const campId = camp?._id?.toString() || user?.campId?.toString() || user?._id?.toString() || '';
                  navigate(campId ? `/camp/${campId}/profile` : '/camp/profile');
                }}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
              >
                <Edit className="w-4 h-4" />
                Edit Camp to Make Public
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Back Button and Edit Button */}
      <div className="mb-6 flex justify-between items-center">
        <Button 
          onClick={() => navigate('/camps')} 
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Camps
        </Button>
        
        {isCampOwner && (
          <Button 
            onClick={() => {
              const campId = camp?._id?.toString() || user?.campId?.toString() || user?._id?.toString() || '';
              navigate(campId ? `/camp/${campId}/profile` : '/camp/profile', { state: { editMode: true } });
            }}
            className="flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit Camp
          </Button>
        )}
      </div>

      {/* Hero Section */}
      <Card className="mb-8">
        <div className="p-8">
          {/* Camp Photo */}
          {camp.photos && camp.photos.length > 0 && (() => {
            const photo = camp.photos[Math.min(camp.primaryPhotoIndex || 0, camp.photos.length - 1)];
            const photoUrl = typeof photo === 'string' ? photo : photo.url;
            return (
              <div className="mb-6">
                <img
                  src={photoUrl}
                  alt={camp.campName || 'Camp Photo'}
                  className="w-full h-64 object-cover rounded-lg"
                  onError={(e) => {
                    console.error('🖼️ [PublicCampProfile] Image failed to load:', e.currentTarget.src);
                  }}
                  onLoad={() => {
                    console.log('✅ [PublicCampProfile] Image loaded successfully:', photoUrl);
                  }}
                />
              </div>
            );
          })()}

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              {/* 1. Title */}
              <h1 className="text-h1 font-lato-bold text-custom-text mb-4">
                {camp.campName}
              </h1>
              
              {/* 2. Camp Hometown, Burning Since, Number of Members */}
              <div className="flex flex-wrap gap-3 mb-6">
                {camp.hometown && (
                  <Badge variant="neutral" className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {camp.hometown}
                  </Badge>
                )}
                {camp.burningSince && (
                  <Badge variant="neutral" className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Burning since {camp.burningSince}
                  </Badge>
                )}
                {camp.stats?.totalMembers && (
                  <Badge variant="neutral" className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {camp.stats.totalMembers} members
                  </Badge>
                )}
              </div>

              {/* 3. Socials & Website */}
              {(camp.website || camp.socialMedia?.facebook || camp.socialMedia?.instagram || camp.socialMedia?.twitter || camp.socialMedia?.tiktok) && (
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-3">
                    {camp.website && (
                      <a
                        href={camp.website.startsWith('http://') || camp.website.startsWith('https://') ? camp.website : `https://${camp.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-custom-primary/10 hover:bg-custom-primary/20 text-custom-primary transition-colors"
                        title="Website"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                    {camp.socialMedia?.facebook && (
                      <a
                        href={camp.socialMedia.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                        title="Facebook"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {camp.socialMedia?.instagram && (
                      <a
                        href={camp.socialMedia.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-pink-100 text-pink-600 hover:bg-pink-200 transition-colors"
                        title="Instagram"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {camp.socialMedia?.twitter && (
                      <a
                        href={camp.socialMedia.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-400 hover:bg-blue-100 transition-colors"
                        title="Twitter"
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                    {camp.socialMedia?.tiktok && (
                      <a
                        href={camp.socialMedia.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        title="TikTok"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* 4. Playa Location */}
              {camp.location && (camp.location.street || camp.location.time || camp.location.description) && (
                <div className="mb-6">
                  <h3 className="text-h3 font-lato-bold text-custom-text mb-3">Playa Location</h3>
                  <div className="space-y-2">
                    {(camp.location.time || camp.location.street) && (
                      <div className="flex items-start gap-2">
                        <Home className="w-5 h-5 text-custom-text-secondary mt-0.5" />
                        <div>
                          {camp.location.time && <p className="text-body text-custom-text">{camp.location.time}</p>}
                          {camp.location.street && <p className="text-body text-custom-text">{camp.location.street}</p>}
                          {camp.location.crossStreet && (
                            <p className="text-sm text-custom-text-secondary">Near: {camp.location.crossStreet}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {camp.location.description && (
                      <p className="text-sm text-custom-text-secondary ml-7">{camp.location.description}</p>
                    )}
                  </div>
                </div>
              )}

              {/* 5. Categories */}
              {camp.categories && camp.categories.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-h3 font-lato-bold text-custom-text mb-3">Categories</h3>
                  <div className="flex flex-wrap gap-2">
                    {camp.categories.map((category) => (
                      <Badge key={category._id} variant="neutral" className="px-3 py-1">
                        {category.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* 6. About */}
              {camp.description && (
                <div className="mb-6">
                  <h3 className="text-h3 font-lato-bold text-custom-text mb-2">About</h3>
                  <p className="text-body text-custom-text-secondary">{camp.description}</p>
                </div>
              )}

              {/* 7. Shared Amenities */}
              {camp.selectedPerks && camp.selectedPerks.filter(sp => sp.isOn && sp.offering).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-h3 font-lato-bold text-custom-text mb-3">Shared Amenities</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {camp.selectedPerks
                      .filter(sp => sp.isOn && sp.offering)
                      .map((selectedPerk) => (
                        <div
                          key={selectedPerk.perkId}
                          className="flex items-center gap-2"
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded ${selectedPerk.offering!.color}`}>
                            {renderIcon(selectedPerk.offering!.icon)}
                          </div>
                          <span className="font-medium text-sm text-custom-text">{selectedPerk.offering!.name}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Apply Button - Only show for personal accounts or non-logged in users */}
            <div className="lg:ml-8">
              {isCampOwner ? (
                // Camp owner viewing their own profile
                <div className="text-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-custom-text font-medium mb-3">
                    This is your camp's public facing profile
                  </p>
                  <Button 
                    onClick={() => {
                      const campId = camp?._id?.toString() || user?.campId?.toString() || user?._id?.toString() || '';
                      navigate(campId ? `/camp/${campId}/profile` : '/camp/profile', { state: { editMode: true } });
                    }}
                    size="lg"
                    className="w-full lg:w-auto"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Camp
                  </Button>
                </div>
              ) : camp.acceptingApplications ? (
                // Apply only for personal accounts or guests; camp logins cannot apply
                ((user?.accountType === 'personal' || !user) && !isShiftsOnlyApplicationLocked) ? (
                  <Button 
                    onClick={handleApplyNow}
                    size="lg"
                    className="w-full lg:w-auto"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Apply Now
                  </Button>
                ) : user?.accountType === 'camp' ? (
                  <p className="text-sm text-custom-text-secondary text-center lg:text-left">
                    Member accounts can apply
                  </p>
                ) : isShiftsOnlyApplicationLocked && !isEventsLeadViewingAssignedCamp ? (
                  <p className="text-sm text-custom-text-secondary text-center lg:text-left">
                    Shifts-only members can apply to camps after September 15
                  </p>
                ) : null
              ) : (
                // Camp is not accepting applications
                user?.accountType === 'personal' || !user ? (
                  <div className="text-center p-4 bg-gray-100 rounded-lg">
                    <p className="text-custom-text-secondary">
                      Not currently accepting applications
                    </p>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Application Modal */}
      <Modal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        title={`Apply to ${camp.campName}`}
        size="lg"
      >
        {applicationSuccess ? (
          <div className="text-center py-8">
            <div className="mb-4 animate-bounce">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
            </div>
            <h3 className="text-2xl font-lato-bold text-custom-text mb-3">
              🎉 Application Submitted Successfully!
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 max-w-md mx-auto">
              <p className="text-custom-text mb-2">
                <strong>Great news!</strong> Your application to join <strong>{camp.campName}</strong> has been received.
              </p>
              <p className="text-sm text-custom-text-secondary">
                The camp leads will review your application and get back to you soon. You'll receive an email notification when there's an update.
              </p>
            </div>
            <p className="text-xs text-custom-text-secondary mt-4">
              This modal will close automatically in a few seconds...
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pre-populated user info */}
            {user && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-custom-text mb-2">Your Information</h4>
                <p className="text-sm text-custom-text-secondary">
                  {user.firstName} {user.lastName} • {user.email}
                </p>
              </div>
            )}

            {/* Motivation */}
            {camp.applicationInstructions && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-medium text-custom-text mb-2">Application Instructions</h4>
                <p className="text-sm text-custom-text-secondary whitespace-pre-wrap">
                  {camp.applicationInstructions}
                </p>
              </div>
            )}

            <div>
              <label className="block text-label font-medium text-custom-text mb-2">
                Why do you want to join {camp.campName}? (Optional)
              </label>
              <textarea
                value={applicationData.motivation}
                onChange={(e) => setApplicationData(prev => ({ ...prev, motivation: e.target.value }))}
                placeholder="Tell us about your motivation to join us, how you can contribute, any past experience working in a camp project, or anything you'd like us to know."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent resize-none"
                rows={5}
              />
            </div>

            {/* Call Time Selection */}
            {availableCallSlots.length > 0 && (
              <div>
                <label className="block text-label font-medium text-custom-text mb-2">
                  Preferred Orientation Call Time (Optional)
                </label>
                <select
                  value={applicationData.selectedCallSlotId}
                  onChange={(e) => setApplicationData(prev => ({ ...prev, selectedCallSlotId: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-primary focus:border-transparent text-gray-900"
                >
                  <option value="">Select a call time...</option>
                  {availableCallSlots.map((slot) => (
                    <option key={slot._id} value={slot._id}>
                      {new Date(slot.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })} • {slot.startTime} - {slot.endTime}
                      {slot.currentParticipants >= slot.maxParticipants ? ' (Full)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-2">
                  Select a time for your orientation call with the camp leadership
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowApplicationModal(false)}
                className="flex-1"
                disabled={applicationLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitApplication}
                className="flex-1"
                disabled={applicationLoading}
              >
                {applicationLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileCompletionModal}
        onClose={() => setShowProfileCompletionModal(false)}
        onComplete={handleProfileComplete}
      />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PublicCampProfile;
